import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Button } from "../../../ui/Button";
import { ScorecardIcon } from "../../../assets/svg/ScorecardIcon";
import { TitleText } from "../../../ui/Text";
import { SettingsIcon } from "../../../assets/svg/SettingsIcon";
import { OrganisationTypeIcon } from "../../../assets/svg/OrganisationTypeIcon";
import { Formik } from "formik";
import { postEndpoint, putEndpoint } from "../../../api/api";
import { SCORECARD_URL, PERIOD_URL } from "../../../api/urls";
import { get } from "../../../utils/get";
import moment from "moment";

import KpiParameters from "./KpiForms/KpiParameters";
import AlignmentOptions from "./KpiForms/AlignmentOptions";
import Allocation from "./KpiForms/Allocation";
import Axios from "../../../utils/axios";
import { toast } from "react-toastify";
import KpiParameters360 from "./KpiForms/KPIParameters360";

interface IProps {
  kpi?: any;
  scorecard_id: string;
  editData?: any;
  isAddMode: any;
  isEdit: any;
  closeModal?: any;
}

const AddKpi360 = ({
  kpi,
  scorecard_id,
  editData,
  isEdit,
  isAddMode,
  closeModal,
}: IProps) => {
  const [createRes, createReq] = postEndpoint();
  const [updateRes, updateReq] = putEndpoint();
  const [scorecard, setScorecard] = useState<any>([]);
  const [period, setPeriod] = useState<any>([]);
  const formRef: any = useRef<any>(null);

  const fetchScorecard = () => {
    Axios.get(`${SCORECARD_URL}`).then((res) => {
      const { returned_resultset } = get(res, "data");
      setScorecard(
        returned_resultset.filter((scorecard) => scorecard.id === scorecard_id)
      );
    });
  };

  useEffect(() => {
    fetchScorecard();
    return () => {};
  }, []);

  useEffect(() => {
    if (scorecard.length > 0) {
      Axios.get(`${PERIOD_URL}`).then((res) => {
        const { returned_resultset } = get(res, "data");
        const id = get(scorecard[0], "period_id");

        setPeriod(returned_resultset.filter((period) => period.id === id));
      });
    }
    return () => {};
  }, [scorecard]);

  const renderSteps = (values, setFieldValue) => {
    return (
      <KpiParameters360
        kpiOptions={kpi}
        values={values}
        setFieldValue={setFieldValue}
      />
    );
  };

  const getTargets = (values) => {
    if (
      get(values, "allocation.value") === "monthly" ||
      get(values, "allocation.value") === "prorata-monthly" ||
      get(values, "allocation.value") === "same-all-periods"
    ) {
      return moment.monthsShort().map((month) => {
        const target = {
          end_date: moment().month(month).endOf("month").format("YYYY-MM-DD"),
          label: month,
          // period_id: null,
          start_date: moment()
            .month(month)
            .startOf("month")
            .format("YYYY-MM-DD"),
          target: parseFloat(get(values, month).replace(/,/g, "")),
        };

        return target;
      });
    } else {
      return ["Q1", "Q2", "Q3", "Q4"].map((i, index) => {
        const date = moment(get(period[0], "start_date")).format("YYYY");

        const target = {
          end_date: moment(date)
            .quarter(index + 1)
            .endOf("quarter")
            .format("YYYY-MM-DD"),
          label: `${i}`,
          start_date: moment(date)
            .quarter(index + 1)
            .startOf("quarter")
            .format("YYYY-MM-DD"),
          target: parseFloat(get(values, i)),
        };

        return target;
      });
    }
  };

  const handleAddKpi = (values) => {
    const payload: any = {
      kpi_id: get(values, "kpi.value"),
      weight: parseInt(get(values, "weight", 0)),
    };
    createReq(`${SCORECARD_URL}/${scorecard_id}/kpi`, payload);
  };

  useEffect(() => {
    const { complete, pending, data, error } = createRes;
    if (error) {
      toast.error(get(data, "message"));
    }
    if (!pending && complete && !error) {
      closeModal();
      toast.success("Kpi added successfully");
    }

    return () => {};
  }, [createRes]);

  const editKpi = (values) => {
    const payload: any = {
      // kpi_id: get(values, "kpi.value"),
      weight: get(values, "weight"),
    };

    updateReq(
      `${SCORECARD_URL}/${scorecard_id}/kpi/${editData.kpi_id}`,
      payload
    );
  };
  useEffect(() => {
    const { complete, pending, data, error } = updateRes;
    if (error) toast.error(get(data, "message"));
    if (!pending && complete && !error) {
      toast.success("Kpi updated successfully");
      closeModal();
    }
  }, [updateRes]);

  useEffect(() => {
    if (!isAddMode && isEdit) {
      const newEditData = {
        kpi: {
          value: get(editData, "kpi.id"),
          label: get(editData, "kpi_name"),
        },
        weight: get(editData, "weight"),
      };

      const fields = ["kpi", "weight"];

      fields.forEach((field) =>
        formRef.current.setFieldValue(field, newEditData[field], false)
      );
    }
  }, [isAddMode]);

  const initialValues = {
    kpi: "",
    weight: "",
  };
  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={isAddMode ? handleAddKpi : editKpi}
    >
      {({ handleSubmit, setFieldValue, values }) => (
        <>
          <Header>
            <HeaderItem enabled={true}>
              <ScorecardIcon isActive />
              <TitleText>KPI parameters</TitleText>
            </HeaderItem>
          </Header>
          {renderSteps(values, setFieldValue)}
          <Footer>
            <Button text="Cancel" backgroundColor="#fff" color="#214160" />
            <SmallButton text="Finish" onClick={() => handleSubmit()} />
          </Footer>
        </>
      )}
    </Formik>
  );
};

export default AddKpi360;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  /* justify-content: space-between; */
  align-items: center;
  border: 2px solid rgba(76, 92, 251, 0.1);
  border-radius: 1em;
  margin-top: 1em;
  margin-bottom: 1em;
`;

const HeaderItem = styled.button<{ enabled: boolean }>`
  display: flex;
  font-size: 12;
  font-weight: 600;
  align-items: center;
  background-color: ${(props) => (props.enabled ? "#EBEFFF" : "#fff")};
  color: #01A8CE;
  padding: 1em 1.5em 1em 1.5em;
  border-radius: 1em;
  width: 100%;
  box-shadow: 0px 0px 0px transparent;
  border: 0px solid transparent;
  text-shadow: 0px 0px 0px transparent;
  opacity: ${(props) => (props.enabled ? 1 : 0.4)};
  ${TitleText} {
    padding-left: 1em;
    color: #01A8CE;
    font-size: 12px;
  }
  &:focus {
    outline: none;
  }
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1em;
`;

const ButtonWithBorder = styled(Button)<{ disabled: boolean }>`
  border: 2px solid #01A8CE;
  padding: 0.3em 4em;
  margin-left: auto;
  margin-right: 1em;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
`;

const SmallButton = styled(Button)`
  padding: 0.4em 4em;
  border: 2px solid #01A8CE;
`;
